<template>
  <div class="modal-overlay" @click="closeModal">
    <div class="modal-content" @click.stop>
      <!-- Modal Header -->
      <div class="modal-header" >
        <div class="header-content">
          <v-icon class="header-icon" size="32">
            {{ chat.isGroup ? 'mdi-account-group' : 'mdi-message-text' }}
          </v-icon>
          <div class="header-text">
            <h3 class="header-title">Chat Settings</h3>
          </div>
        </div>

        <button @click="$emit('close')" class="btn-close" :disabled="loading">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">
        <!-- Chat Info Section -->
        <div class="settings-section">
          <div class="section-header">
            <v-icon class="section-icon" size="24">mdi-information-outline</v-icon>
            <h4 class="section-title">Chat Information</h4>
          </div>
          
          <div class="info-card">
            <div class="chat-name-display">
              <div class="chat-avatar">
                {{ getChatInitials() }}
              </div>
              <div class="chat-details">
                <div class="chat-name">{{ getChatName() }}</div>
                <div class="chat-meta">
                  <v-icon size="14">
                    {{ chat.isGroup ? 'mdi-account-group' : 'mdi-account' }}
                  </v-icon>
                  <span>{{ chat.isGroup ? 'Group Chat' : 'Direct Chat' }}</span>
                </div>
              </div>
            </div>

            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">
                  <v-icon left size="18">mdi-account-multiple</v-icon>
                  <span>Members</span>
                </div>
                <div class="info-value">{{ chat.users.length }}</div>
              </div>
              
              <div v-if="chat.isGroup" class="info-item">
                <div class="info-label">
                  <v-icon left size="18" color="warning">mdi-crown</v-icon>
                  <span>Admin</span>
                </div>
                <div class="info-value">{{ getAdminName() }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Group Name Edit -->
        <div v-if="chat.isGroup && isAdmin" class="settings-section">
          <div class="section-header">
            <v-icon class="section-icon" size="24">mdi-pencil</v-icon>
            <h4 class="section-title">Group Settings</h4>
          </div>
          <p class="section-description">
            Change the group name (visible to all members)
          </p>
          
          <div class="setting-card">
            <label class="setting-label">Group Name</label>
            <div class="input-with-button">
              <div class="input-wrapper">
                <input 
                  v-model="editedGroupName"
                  placeholder="Enter group name..."
                  class="form-input"
                  maxlength="50"
                  @keyup.enter="updateGroupName"
                />
                <div class="char-count">{{ editedGroupName.length }}/50</div>
              </div>
              <v-btn
                @click="updateGroupName"
                :disabled="!editedGroupName.trim() || editedGroupName === chat.groupName"
                variant="outlined"
                color="success"
                prepend-icon="mdi-check"
                class="update-btn"
              >
                Update
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Members Section -->
        <div class="settings-section">
          <div class="section-header">
            <v-icon class="section-icon" size="24">mdi-account-multiple</v-icon>
            <h4 class="section-title">Members</h4>
            <span class="members-badge">{{ chat.users.length }}</span>
            <v-spacer></v-spacer>
            <v-btn
              v-if="chat.isGroup && isAdmin"
              @click="showAddUser = true"
              variant="outlined"
              prepend-icon="mdi-account-plus"
              size="small"
              class="add-member-btn"
            >
              Add Member
            </v-btn>
          </div>

          <div class="members-list">
            <div 
              v-for="userId in chat.users"
              :key="userId[0]"
              class="member-item"
              :class="{ 
                'is-you': userId[0] === getCurrentUserId,
                'is-admin': userId[0] === chat.adminId 
              }"
            >
              <!-- Member Info -->
              <div class="member-info">
                <div class="member-avatar">
                  <span class="avatar-text">{{ getUserInitialsChat(userId) }}</span>
                  <v-icon 
                    v-if="userId[0] === chat.adminId" 
                    size="14" 
                    color="warning"
                    class="admin-crown"
                  >
                    mdi-crown
                  </v-icon>
                </div>
                <div class="member-details">
                  <div class="member-name-row">
                    <span class="member-name">{{ getUserNameChat(userId) }}</span>
                    <v-chip 
                      v-if="userId[0] === getCurrentUserId" 
                      size="x-small"
                      color="success"
                      class="you-chip"
                    >
                      You
                    </v-chip>
                  </div>
                  <div class="member-status">
                    <span :class="{ online: getUserChatStatusComputed(userId[0]) }" class="status-dot"></span>
                    <span class="status-text">
                      {{ getUserChatStatusComputed(userId[0]) ? 'Online' : 'Offline' }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Member Actions -->
              <div v-if="userId[0] !== getCurrentUserId" class="member-actions">
                <!-- Admin Actions -->
                <template v-if="chat.isGroup && isAdmin">
                  <v-btn
                    v-if="userId[0] !== chat.adminId"
                    @click="makeAdmin(userId[0])"
                    variant="text"
                    size="small"
                    icon
                    class="action-icon"
                    title="Make Admin"
                  >
                    <v-icon size="20" color="warning">mdi-crown</v-icon>
                  </v-btn>
                  <v-btn
                    @click="removeMember(userId[0])"
                    variant="text"
                    size="small"
                    icon
                    class="action-icon danger"
                    title="Remove Member"
                  >
                    <v-icon size="20" color="error">mdi-account-remove</v-icon>
                  </v-btn>
                </template>

                <!-- Regular User Actions -->
                <template v-else>
                  <v-btn
                    @click="viewProfile(userId[1])"
                    variant="text"
                    size="small"
                    icon
                    class="action-icon"
                    title="View Profile"
                  >
                    <v-icon size="20">mdi-account-circle</v-icon>
                  </v-btn>
                  <v-btn
                    v-if="blockedUsers.some(blocked => blocked[0] === userId[0])"
                    @click="unblockUser(userId[0])"
                    variant="text"
                    size="small"
                    icon
                    class="action-icon"
                    title="Unblock User"
                  >
                    <v-icon size="20" color="success">mdi-message-check-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    @click="blockUser(userId[0])"
                    variant="text"
                    size="small"
                    icon
                    class="action-icon danger"
                    title="Block User"
                  >
                    <v-icon size="20" color="error">mdi-cancel</v-icon>
                  </v-btn>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Actions -->
        <div class="settings-section last">
          <div class="section-header">
            <v-icon class="section-icon" size="24">mdi-cog-outline</v-icon>
            <h4 class="section-title">Actions</h4>
          </div>
          
          <div class="actions-grid">
            <v-btn
              @click="hideChat"
              variant="outlined"
              prepend-icon="mdi-eye-off"
              block
              class="action-btn"
            >
              Hide Chat
            </v-btn>
            
            <v-btn
              v-if="chat.isGroup"
              @click="leaveChat"
              variant="outlined"
              color="warning"
              prepend-icon="mdi-exit-to-app"
              block
              class="action-btn"
            >
              Leave Group
            </v-btn>
            
            <v-btn
              @click="deleteChat"
              variant="outlined"
              color="error"
              prepend-icon="mdi-delete-outline"
              block
              class="action-btn"
            >
              Delete Chat
            </v-btn>
            
            <v-btn
              v-if="chat.isGroup && isAdmin"
              @click="deleteGroupPermanently"
              variant="outlined"
              color="error"
              prepend-icon="mdi-delete-alert"
              block
              class="action-btn danger-strong"
            >
              Delete Permanently
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Add User Modal -->
      <div v-if="showAddUser" class="add-user-overlay">
        <div class="add-user-modal" @click.stop>
          <div class="add-user-header">
            <div class="header-content-small">
              <v-icon class="header-icon-small" size="24">mdi-account-plus</v-icon>
              <h4 class="header-title-small">Add Member</h4>
            </div>
            <button @click="showAddUser = false" class="btn-close-small">
              <v-icon size="18">mdi-close</v-icon>
            </button>
          </div>
          
          <div class="add-user-body">
            <div class="search-wrapper">
              <v-icon class="search-icon" size="18">mdi-magnify</v-icon>
              <input 
                v-model="searchQuery"
                @input="searchUsers"
                placeholder="Search users by username..."
                class="search-input"
              />
            </div>
            
            <!-- Search Hint -->
            <div v-if="searchQuery.length > 0 && searchQuery.length < 3" class="search-hint">
              <v-icon left size="16" color="warning">mdi-information</v-icon>
              <span>Type at least 3 characters to search</span>
            </div>

            <!-- Search Results -->
            <div v-if="searchResults.length > 0" class="search-results">
              <div class="results-header">
                <v-icon left size="16">mdi-account-multiple</v-icon>
                <span>Found {{ searchResults.length }} user(s)</span>
              </div>
              <div class="users-list">
                <div 
                  v-for="user in searchResults"
                  :key="user._id"
                  @click="addUserToChat(user._id)"
                  class="user-search-item"
                >
                  <div class="user-info">
                    <div class="user-avatar">
                      {{ getUserInitials(user.verse_username || 'Unknown') }}
                    </div>
                    <div class="user-details">
                      <div class="user-name">{{ user.verse_username || 'Unknown' }}</div>
                    </div>
                  </div>
                  <v-icon size="20" color="success">mdi-plus-circle</v-icon>
                </div>
              </div>
            </div>

            <!-- Empty Search -->
            <div v-else-if="searchQuery.length >= 3" class="empty-search">
              <v-icon size="48" color="grey">mdi-account-search-outline</v-icon>
              <p class="empty-text">No users found</p>
              <p class="empty-hint">Try a different search term</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import chatService from '@/services/chat/ChatService'
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useMobile } from '@/composables/useMobile.js'
import { useMobileXs } from '@/composables/useMobileXs.js'

export default {
  setup() {
    const isMobile = useMobile()
    const isMobileXs = useMobileXs()

    const notify = (message) => {
      toast(message, {
        theme: "dark",
        type: "success",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 1500,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyError = (message) => {
      toast(message, {
        theme: "dark",
        type: "error",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyWarning = (message) => {
      toast(message, {
        theme: "dark",
        type: "warning",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    return { isMobile, isMobileXs, notify, notifyError, notifyWarning }
  },
  name: 'ChatSettingsModal',
  props: {
    chat: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'user-added', 'user-removed', 'chat-left', 'chat-updated'],
  data() {
    return {
      editedGroupName: '',
      showAddUser: false,
      searchQuery: '',
      searchResults: [],
      loading: false,
      loadingMessage: '',
            
      privacySettings: {
        allowMessagesFromAll: true,
        blockedUsers: [],
        following: []
      },
      
      blockedUsers: [],
      searchTimeout: null,
    }
  },
  computed: {
    getCurrentUserId() {
      return this.$store.getters.getUserData.id || 'current-user-id';
    },
    isLogged() {
      return this.$store.getters.getUserData != null
    },
    isAdmin() {
      return this.chat.isGroup && this.chat.adminId === this.getCurrentUserId
    },
    getUserChatStatusComputed() {
      return (userId) => {
        if (!userId) return false
        return chatService.userStatusesCache.get(userId) || false
      }
    }
  },
  async mounted() {
    this.editedGroupName = this.chat.groupName || ''
    
    this.chat.users.forEach(async (user) => {
      await chatService.getUserStatusCache(user[0])
    })
    
    const privacy = await chatService.getPrivacySettings()
    this.privacySettings = privacy
    this.blockedUsers = privacy.blockedUsers || []
  },

  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
  },
  
  methods: {
    closeModal() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
        this.searchTimeout = null
      }
      this.$emit('close')
    },

    getChatName() {
      if (this.chat.isGroup) {
        return this.chat.groupName || `Group (${this.chat.users.length} members)`
      }
      const otherUser = this.chat.users.find(user => user[0] !== this.getCurrentUserId)
      return otherUser ? otherUser[1] : 'Unknown User'
    },

    getChatInitials() {
      const name = this.getChatName()
      return name.substring(0, 2).toUpperCase()
    },

    getAdminName() {
      if (this.chat.adminId === this.getCurrentUserId) return 'You'
      const adminUser = this.chat.users.find(user => user[0] === this.chat.adminId)
      return adminUser ? adminUser[1] : 'Unknown Admin'
    },

    getUserNameChat(user) {
      return `${user[1].substring(0, 20)}${user[1].length > 20 ? '...' : ''}`
    },

    getUserInitials(name) {
      return name.substring(0, 2).toUpperCase()
    },

    getUserInitialsChat(user) {
      const name = `${user[1]}`
      return name.substring(0, 2).toUpperCase()
    },

    async updateGroupName() {
      if (!this.editedGroupName.trim() || this.editedGroupName === this.chat.groupName) return

      try {
        this.loading = true
        this.loadingMessage = 'Updating group name...'

        await chatService.updateGroupName(this.chat._id, this.editedGroupName)

        this.$emit('chat-updated')
        this.showToast('Group name updated successfully', 'success')

      } catch (error) {
        this.showToast('Failed to update group name', 'error')
      } finally {
        this.loading = false
      }
    },

    async searchUsers() {
      if (this.searchQuery.length < 3) {
        this.searchResults = []
        return
      }

      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(async () => {
        try {
          chatService.usersSearch(this.searchQuery.trim())
            .then(response => {
              this.searchResults = response.filter(user =>
                !this.blockedUsers.some(blocked => blocked[0] === user._id) &&
                !this.chat.users.some(chatUser => chatUser[0] === user._id)
              )
            })
            .catch(error => {
              this.searchResults = []
            })
        } catch (error) {
          this.searchResults = []
        }
      }, 300)
    },

    async addUserToChat(userId) {
      try {
        this.loading = true
        this.loadingMessage = 'Adding user to chat...'

        await chatService.addUserToChat(this.chat._id, userId)

        this.showAddUser = false
        this.searchQuery = ''
        this.searchResults = []
        
        this.$emit('user-added')
        this.showToast('User added successfully', 'success')

      } catch (error) {
        this.showToast(error.response?.data?.error || 'Failed to add user', 'error')
      } finally {
        this.loading = false
      }
    },

    async removeMember(userId) {
      try {
        this.loading = true
        this.loadingMessage = 'Removing user...'

        await chatService.removeUserFromChat(this.chat._id, userId)

        this.$emit('user-removed')
        this.showToast('User removed successfully', 'success')

      } catch (error) {
        this.showToast(error.response?.data?.error || 'Failed to remove user', 'error')
      } finally {
        this.loading = false
      }
    },

    async makeAdmin(userId) {
      try {
        this.loading = true
        this.loadingMessage = 'Updating admin...'

        await chatService.transferAdmin(this.chat._id, userId)

        this.$emit('chat-updated')
        this.showToast('Admin rights transferred', 'success')

      } catch (error) {
        this.showToast('Failed to transfer admin rights', 'error')
      } finally {
        this.loading = false
      }
    },

    async hideChat() {
      try {
        this.loading = true
        this.loadingMessage = 'Hiding chat...'

        await chatService.hideChat(this.chat._id)

        this.$emit('close')
        chatService.gotoEmptyChat()
        this.showToast('Chat hidden successfully', 'success')

      } catch (error) {
        this.showToast('Failed to hide chat', 'error')
      } finally {
        this.loading = false
      }
    },

    async leaveChat() {
      try {
        this.loading = true
        this.loadingMessage = 'Leaving group...'

        await chatService.leaveChat(this.chat._id)

        this.$emit('chat-left')
        this.$emit('close')
        chatService.gotoEmptyChat()
        this.showToast('Left group successfully', 'success')

      } catch (error) {
        this.showToast('Failed to leave group', 'error')
      } finally {
        this.loading = false
      }
    },

    async deleteChat() {
      try {
        this.loading = true
        this.loadingMessage = 'Deleting chat...'

        await chatService.deleteChat(this.chat._id)

        this.$emit('close')
        chatService.gotoEmptyChat()

        this.showToast('Chat deleted successfully', 'success')

      } catch (error) {
        this.showToast('Failed to delete chat', 'error')
      } finally {
        this.loading = false
      }
    },

    async deleteGroupPermanently() {
      try {
        this.loading = true
        this.loadingMessage = 'Permanently deleting group...'

        await chatService.deleteGroupPermanently(this.chat._id)

        this.$emit('close')
        chatService.gotoEmptyChat()
        this.showToast('Group permanently deleted', 'success')

      } catch (error) {
        this.showToast('Failed to permanently delete group', 'error')
      } finally {
        this.loading = false
      }
    },

    async viewProfile(userId) {
      window.open(`https://verse.muzible.com/users/${userId}`, '_blank');
    },

    async blockUser(userId) {
      try {
        this.loading = true
        this.loadingMessage = 'Blocking user...'
        await chatService.blockUser(userId)

        this.blockedUsers.push([userId, 'User'])
        this.showToast('User blocked successfully', 'success')

      } catch (error) {
        this.showToast('Failed to block user', 'error')
      } finally {
        this.loading = false
      }
    },

    async unblockUser(userId) {
      try {
        this.loading = true
        this.loadingMessage = 'Unblocking user...'

        await chatService.unblockUser(userId)
        this.blockedUsers = this.blockedUsers.filter(blocked => blocked[0] !== userId)

        this.showToast('User unblocked successfully', 'success')

      } catch (error) {
        this.showToast('Failed to unblock user', 'error')
      } finally {
        this.loading = false
      }
    },

    showToast(message, type = 'info') {
      if (type === 'error') {
        this.notifyError(message)
      } else if (type === 'warning'){
        this.notifyWarning(message)
      } else {
        this.notify(message)
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@use '../../views/css/styles.scss';

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease;
}

/* Modal Content */
.modal-content {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
}

/* Modal Header */
.modal-header {
  padding: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
  transition: background 0.3s ease;
}

.modal-header.updating {
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.3), rgba(0, 86, 179, 0.3));
  border-left: 3px solid #007bff;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.header-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  animation: slideInLeft 0.3s ease;
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.header-icon {
  color: white !important;
  flex-shrink: 0;
}

.modal-header.updating .header-icon {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.header-title {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.loading-text {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.btn-close {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: white;
  flex-shrink: 0;
}

.btn-close:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

.btn-close:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Modal Body */
.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

/* Settings Section */
.settings-section {
  margin-bottom: 28px;
  padding-bottom: 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  animation: slideIn 0.3s ease;
}

.settings-section.last {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.section-icon {
  color: white !important;
}

.section-title {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: white;
  flex: 1;
}

.members-badge {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 12px;
  font-family: "Darker Grotesque", sans-serif;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.4);
}

.section-description {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.add-member-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.add-member-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Info Card */
.info-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.chat-name-display {
  display: flex;
  align-items: center;
  gap: 14px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.chat-avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.chat-details {
  flex: 1;
  min-width: 0;
}

.chat-name {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin-bottom: 6px;
  word-break: break-word;
}

.chat-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 14px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);
}

.info-value {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: white;
  font-weight: 500;
}

/* Setting Card */
.setting-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 16px;
}

.setting-label {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: white;
  display: block;
  margin-bottom: 10px;
}

.input-with-button {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.input-wrapper {
  position: relative;
  flex: 1;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: white;
  outline: none;
  transition: all 0.3s ease;
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.form-input:focus {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

.char-count {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.update-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  flex-shrink: 0;
}

/* Members List */
.members-list {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  overflow: hidden;
}

.member-item {
  padding: 14px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s ease;
}

.member-item:last-child {
  border-bottom: none;
}

.member-item:hover {
  background: rgba(0, 123, 255, 0.1);
}

.member-item.is-you {
  background: rgba(40, 167, 69, 0.1);
  border-left: 3px solid #28a745;
}

.member-item.is-admin {
  background: rgba(255, 193, 7, 0.1);
  border-left: 3px solid #ffc107;
}

.member-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.member-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
  position: relative;
}

.admin-crown {
  position: absolute;
  top: -4px;
  right: -4px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 50%;
  padding: 2px;
}

.member-details {
  flex: 1;
  min-width: 0;
}

.member-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.member-name {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.you-chip {
  font-family: "Darker Grotesque", sans-serif !important;
  font-weight: 700 !important;
}

.member-status {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #dc3545;
  animation: pulse 2s ease-in-out infinite;
}

.status-dot.online {
  background: #28a745;
}

.status-text {
  font-size: 12px;
}

.member-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.action-icon {
  transition: all 0.2s ease;
}

.action-icon:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  transform: scale(1.1);
}

.action-icon.danger:hover {
  background: rgba(220, 53, 69, 0.2) !important;
}

/* Actions Grid */
.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.action-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.action-btn.danger-strong {
  border-color: rgba(220, 53, 69, 0.6) !important;
}

.action-btn.danger-strong:hover {
  background: rgba(220, 53, 69, 0.2) !important;
  border-color: #dc3545 !important;
}

/* Add User Modal */
.add-user-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  animation: fadeIn 0.3s ease;
}

.add-user-modal {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  max-height: 500px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;
}

.add-user-header {
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(0, 0, 0, 0.3);
}

.header-content-small {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-icon-small {
  color: white !important;
}

.header-title-small {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: white;
}

.btn-close-small {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: white;
}

.btn-close-small:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.4);
}

.add-user-body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.add-user-body::-webkit-scrollbar {
  width: 6px;
}

.add-user-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

.search-wrapper {
  position: relative;
  margin-bottom: 16px;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: white;
  outline: none;
  transition: all 0.3s ease;
}

.search-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.search-input:focus {
  border-color: rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

.search-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 10px;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
}

.search-results {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

.results-header {
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-family: "Darker Grotesque", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  gap: 8px;
}

.users-list {
  max-height: 250px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.users-list::-webkit-scrollbar {
  width: 6px;
}

.users-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
}

.user-search-item {
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
}

.user-search-item:last-child {
  border-bottom: none;
}

.user-search-item:hover {
  background: rgba(0, 123, 255, 0.15);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007bff, #0056b3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 700;
  font-family: "Darker Grotesque", sans-serif;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-name {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.empty-search {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-text {
  margin: 16px 0 8px 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.empty-hint {
  margin: 0;
  font-family: "Darker Grotesque", sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-height: 90vh;
  }

  .modal-header {
    padding: 20px;
  }

  .header-title {
    font-size: 20px;
  }

  .modal-body {
    padding: 16px 20px;
  }

  .section-title {
    font-size: 18px;
  }

  .actions-grid {
    grid-template-columns: 1fr;
  }

  .info-grid {
    grid-template-columns: 1fr;
  }

  .input-with-button {
    flex-direction: column;

    .update-btn {
      width: 100%;
    }
  }

  .add-user-modal {
    width: 95%;
  }
}

/* Vuetify Overrides */
:deep(.v-btn:hover) > .v-btn__overlay {
  opacity: 0.1;
}

:deep(.v-chip) {
  font-family: "Darker Grotesque", sans-serif !important;
}
</style>